<template lang="pug">
  // Desktop menu
  v-menu(v-if="!$vuetify.breakpoint.mobile" v-model="showMenu" offset-y rounded="lg" min-width="240" max-width="640" :close-on-content-click="false" left max-height="444" nudge-bottom="4")
    template(v-slot:activator="{ on }")
      v-btn.round-md(v-on="on" color="white" depressed)
        .text-sm.text-none {{ displayText }}
        v-icon(right) $yzi-chevron-down
    .white
      v-skeleton-loader(v-if="isLoading" type="list-item-three-line" width="200")
      div(v-else)
        // Search input - sticky
        .sticky-search.white.pa-2
          v-text-field.round-xl(v-model="searchQuery" placeholder="Search branch..." hide-details dense outlined clearable)

        .scrollable-content(style="max-height: 300px; overflow-y: auto;")
          // Select All option
          .hover-bg(v-if="!searchQuery" @click="toggleSelectAll")
            .d-flex.align-center.justify-space-between.px-4.py-3
              .text-sm.font-weight-medium All branches
              v-checkbox(:input-value="isAllSelected" :indeterminate="isIndeterminate" hide-details dense @click.stop="toggleSelectAll")
            v-divider.basic300

          // Individual company options
          template(v-if="filteredCompanies.length")
            .hover-bg(v-for="company in filteredCompanies" :key="company.value" @click="toggleCompany(company)")
              .d-flex.align-center.justify-space-between.px-4.py-3
                .text-sm {{ company.name }}
                v-checkbox(:input-value="isCompanySelected(company)" hide-details dense @click.stop="toggleCompany(company)")
              v-divider.basic300
          template(v-else)
            .px-4.py-3.text-sm.neutral500--text No branch found

  // Mobile bottom sheet
  div(v-else)
    v-btn.round-md(color="white" depressed @click="showMenu = true")
      .text-sm.text-none {{ displayText }}
      v-icon(right) $yzi-chevron-down

    v-bottom-sheet(v-model="showMenu" max-width="100%")
      v-card
        v-card-text.pa-0
          v-skeleton-loader(v-if="isLoading" type="list-item-three-line")
          div(v-else)
            // Search input - sticky
            .sticky-search.white.px-2.pb-2.pt-4
              v-text-field.round-xl(v-model="searchQuery" placeholder="Search branch..." hide-details dense outlined clearable autofocus)

            .scrollable-content(style="max-height: 400px; overflow-y: auto;")
              // Select All option
              .hover-bg(v-if="!searchQuery" @click="toggleSelectAll")
                .d-flex.align-center.justify-space-between.px-4.py-3
                  .text-sm.font-weight-medium All branches
                  v-checkbox(:input-value="isAllSelected" :indeterminate="isIndeterminate" hide-details dense @click.stop="toggleSelectAll")
                v-divider.basic300

              // Individual company options
              template(v-if="filteredCompanies.length")
                .hover-bg(v-for="company in filteredCompanies" :key="company.value" @click="toggleCompany(company)")
                  .d-flex.align-center.justify-space-between.px-4.py-3
                    .text-sm {{ company.name }}
                    v-checkbox(:input-value="isCompanySelected(company)" hide-details dense @click.stop="toggleCompany(company)")
                  v-divider.basic300
              template(v-else)
                .px-4.py-3.text-sm.neutral500--text No branch found

</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'CompanyDropdown',
  data: () => ({
    isLoading: true,
    showMenu: false,
    companies: [],
    selectedCompanies: [],
    currentRequest: null,
    searchQuery: ''
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      selectedCompaniesFromStore: 'insight/getSelectedCompanies',
      isHq: 'hq/getIsHq'
    }),
    displayText() {
      if (this.selectedCompanies.length === 0) {
        return 'All branches'
      } else if (this.selectedCompanies.length === this.companies.length) {
        return 'All branches'
      } else if (this.selectedCompanies.length === 1) {
        return this.selectedCompanies[0].name
      } else {
        return `${this.selectedCompanies.length} branch selected`
      }
    },
    isAllSelected() {
      return this.companies.length > 0 && this.selectedCompanies.length === this.companies.length
    },
    isIndeterminate() {
      return this.selectedCompanies.length > 0 && this.selectedCompanies.length < this.companies.length
    },
    filteredCompanies() {
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        return this.companies
      }
      const query = this.searchQuery.toLowerCase().trim()
      return this.companies.filter(company =>
        company.name.toLowerCase().includes(query)
      )
    }
  },
  mounted() {
    // Initialize from store
    if (this.selectedCompaniesFromStore) {
      this.selectedCompanies = [...this.selectedCompaniesFromStore]
    } else {
      this.selectedCompanies = []
    }
    this.fetchCompanies()
  },
  watch: {
    selectedCompaniesFromStore: {
      handler(newValue) {
        if (newValue) {
          this.selectedCompanies = [...newValue]
        } else {
          this.selectedCompanies = []
        }
      },
      deep: true
    },
    showMenu(newValue, oldValue) {
      // When menu is closed (newValue is false) and was previously open (oldValue is true)
      if (!newValue && oldValue) {
        // Clear search query when menu is closed
        this.searchQuery = ''

        // If no companies are selected, select all companies
        if (this.selectedCompanies.length === 0) {
          this.selectedCompanies = [...this.companies]
          this.updateStore()
        }
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),

    fetchCompanies(stopRefetch) {
      // Cancel any existing request
      if (this.currentRequest) {
        this.currentRequest.abort()
      }

      this.isLoading = true

      // Create new AbortController for this request
      const controller = new AbortController()
      this.currentRequest = controller
      const params = {
        hq: this.company.id,
        os: 'clinic',
        fields: 'id,name',
        page_size: -1
      }
      this.$axios.get('/companies', {
        params,
        signal: controller.signal
      })
          .then(resp => {
            if (!resp.data?.results) {
              this.companies = []
              this.isLoading = false
              this.currentRequest = null
              return
            }

            this.companies = resp.data.results.map(item => {
              return {
                name: item.name,
                id: item.id
              }
            })

            // If no companies are selected initially, select all companies by default
            if (this.selectedCompanies.length === 0) {
              this.selectedCompanies = [...this.companies]
              this.updateStore()
            }

            this.isLoading = false
            this.currentRequest = null
          })
          .catch(error => {
            // Don't handle aborted requests as errors
            if (error.name === 'AbortError') {
              return
            }

            if (!stopRefetch) {
              this.fetchCompanies(true)
            } else {
              this.isLoading = false
              this.currentRequest = null
            }
          })
    },
    isCompanySelected(company) {
      return this.selectedCompanies.some(selected => selected.id === company.id)
    },
    toggleCompany(company) {
      const index = this.selectedCompanies.findIndex(selected => selected.id === company.id)
      if (index > -1) {
        this.selectedCompanies.splice(index, 1)
      } else {
        this.selectedCompanies.push(company)
      }
      this.updateStore()
    },
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedCompanies = []
      } else {
        this.selectedCompanies = [...this.companies]
      }
      this.updateStore()
    },
    updateStore() {
      // If all companies are selected or none are selected, set to null (All companies)
      if (this.selectedCompanies.length === 0 || this.selectedCompanies.length === this.companies.length) {
        this.$store.commit('insight/setSelectedCompanies', null)
      } else {
        // Store the selected companies array
        this.$store.commit('insight/setSelectedCompanies', [...this.selectedCompanies])
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  }
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
}

.sticky-search {
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
  position: sticky;
  top: 0;
  z-index: 1;
}

/* Fix for mobile keyboard hiding bottom sheet */
.v-bottom-sheet .v-sheet {
  bottom: env(keyboard-inset-height, 0px) !important;
  max-height: calc(100vh - env(keyboard-inset-height, 0px)) !important;
}

/* Alternative approach using safe-area-inset-bottom for better compatibility */
@supports (bottom: env(keyboard-inset-height)) {
  .v-bottom-sheet .v-sheet {
    bottom: env(keyboard-inset-height, env(safe-area-inset-bottom, 0px)) !important;
    max-height: calc(100vh - env(keyboard-inset-height, env(safe-area-inset-bottom, 0px))) !important;
  }
}
</style>